<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import GalleryMediaTile from '@/components/organizer/galleries/GalleryMediaTile.vue'
import { useGalleryUploadManager } from '@/composables/useGalleryUploadManager'
import type { UploadEntry } from '@/composables/useGalleryUploadManager'
import { useGridReorder } from '@/composables/useGridReorder'
import type { GalleryGridFilter, TileAction, UploadTileAction } from '@/types/galleryUi'
import type { OrganizerGalleryItem } from '@/types/galleries'

const CHUNK = 60

const props = defineProps<{
  items: OrganizerGalleryItem[]
  pending: UploadEntry[]
  selection: Set<string>
  filter: GalleryGridFilter
  coverItemId: string | null
}>()

const emit = defineEmits<{
  select: [id: string, shift: boolean]
  open: [id: string]
  reorder: [ids: string[]]
  action: [payload: TileAction]
  'upload-action': [payload: UploadTileAction]
}>()

const { t } = useI18n()
const manager = useGalleryUploadManager()

const visibleCount = ref(CHUNK)
const columns = ref(3)

function updateColumns(): void {
  const width = window.innerWidth
  columns.value = width >= 1280 ? 6 : width >= 1024 ? 5 : width >= 640 ? 4 : 3
}

onMounted(() => {
  updateColumns()
  window.addEventListener('resize', updateColumns)
})
onBeforeUnmount(() => window.removeEventListener('resize', updateColumns))

watch(
  () => props.filter,
  () => {
    visibleCount.value = CHUNK
  },
)

const filteredItems = computed(() =>
  props.filter === 'missing_alt'
    ? props.items.filter(
        (item) => item.type === 'image' && item.status === 'ready' && (item.alt_text ?? '').trim() === '',
      )
    : props.items,
)

const shownItems = computed(() => filteredItems.value.slice(0, visibleCount.value))
const hasMore = computed(() => filteredItems.value.length > shownItems.value.length)

// Uploads that do not have an item yet render as tiles in front of the items.
const pendingTiles = computed(() =>
  props.pending.filter((entry) => entry.itemId === null && entry.status !== 'cancelled'),
)

const reorderEnabled = computed(() => props.filter === 'all' && props.items.length > 1)
const selectionMode = computed(() => props.selection.size > 0)

const reorder = useGridReorder({
  getIds: () => props.items.map((item) => item.id),
  getColumns: () => columns.value,
  onReorder: (ids) => emit('reorder', ids),
  labels: {
    lifted: (position, total) => t('galleries.reorder.lifted', { position, total }),
    moved: (position, total) => t('galleries.reorder.moved', { position, total }),
    dropped: (position, total) => t('galleries.reorder.dropped', { position, total }),
    cancelled: () => t('galleries.reorder.cancelled'),
  },
})

function positionOf(id: string): number {
  return props.items.findIndex((item) => item.id === id) + 1
}
</script>

<template>
  <div>
    <ul
      class="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3"
      role="list"
      :aria-label="t('galleries.grid.label')"
      data-testid="gallery-media-grid"
    >
      <GalleryMediaTile
        v-for="entry in pendingTiles"
        :key="entry.id"
        :upload="entry"
        :selected="false"
        :selection-mode="false"
        :position="0"
        :total="0"
        @cancel="emit('upload-action', { type: 'cancel', id: entry.id })"
        @retry="emit('upload-action', { type: 'retry', id: entry.id })"
        @remove="emit('upload-action', { type: 'remove', id: entry.id })"
      />
      <GalleryMediaTile
        v-for="item in shownItems"
        :key="item.id"
        :item="item"
        :selected="selection.has(item.id)"
        :selection-mode="selectionMode"
        :position="positionOf(item.id)"
        :total="items.length"
        :is-cover="coverItemId === item.id"
        :preview-url="manager.previewFor(item.id)"
        :reorder-enabled="reorderEnabled"
        :dragging="reorder.draggingId.value === item.id"
        :drag-over="reorder.dragOverId.value === item.id && reorder.draggingId.value !== item.id"
        :lifted="reorder.liftedId.value === item.id"
        @toggle-select="(event) => emit('select', item.id, event.shiftKey)"
        @open="emit('open', item.id)"
        @retry="emit('action', { type: 'retry', id: item.id })"
        @hide-toggle="emit('action', { type: 'hide-toggle', id: item.id })"
        @set-cover="emit('action', { type: 'set-cover', id: item.id })"
        @move-to="emit('action', { type: 'move-to', id: item.id })"
        @move-to-start="emit('action', { type: 'move-to-start', id: item.id })"
        @move-to-end="emit('action', { type: 'move-to-end', id: item.id })"
        @delete="emit('action', { type: 'delete', id: item.id })"
        @drag-start="(event) => reorder.onDragStart(item.id, event)"
        @drag-over="(event) => reorder.onDragOver(item.id, event)"
        @drop="(event) => reorder.onDrop(item.id, event)"
        @drag-end="reorder.onDragEnd()"
        @handle-keydown="(event) => reorder.onHandleKeydown(item.id, event)"
      />
    </ul>

    <p v-if="filter === 'missing_alt' && filteredItems.length === 0" class="mt-6 text-center text-sm text-slate-400">
      {{ t('galleries.grid.no_missing_alt') }}
    </p>

    <div v-if="hasMore" class="mt-6 flex justify-center">
      <button
        type="button"
        class="border border-slate-600 hover:border-slate-500 text-slate-200 font-semibold rounded-lg px-4 py-2 text-sm transition-colors"
        @click="visibleCount += CHUNK"
      >
        {{ t('galleries.grid.load_more', { count: filteredItems.length - shownItems.length }) }}
      </button>
    </div>

    <p class="sr-only" aria-live="assertive" data-testid="reorder-announcer">{{ reorder.announcement.value }}</p>
  </div>
</template>
